import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, MapPin, ChevronDown } from "lucide-react";
import { HERO_IMAGES, scrollToId } from "../data/site";
import { EASE } from "./motion";
import { useLang } from "../context/LangContext";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { S, lang } = useLang();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    scrollToId(hash);
  };

  return (
    <section id="accueil" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-[#060F1F] grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24 -bottom-24">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={HERO_IMAGES[slide].src}
            alt={HERO_IMAGES[slide].alt[lang]}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1.12 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.6, ease: "easeInOut" },
              scale: { duration: 8, ease: "linear" },
            }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#060F1F] via-[#0A182E]/72 to-[#0A182E]/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#060F1F]/80 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full pb-28 pt-44">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="flex items-center gap-2 text-sky-400"
        >
          <MapPin className="w-4 h-4" />
          <span className="font-tech text-xs font-semibold uppercase tracking-[0.28em]">
            {S.hero.location}
          </span>
        </motion.div>

        <h1 key={lang} className="mt-6 font-display font-extrabold tracking-tight leading-[1.04] text-white">
          <MaskedLine delay={0.25} className="text-4xl sm:text-5xl lg:text-7xl">
            <span className="inline-flex items-center gap-4 sm:gap-6">
              <img src="/logo-emblem-dark.png" alt="Emblème GPN Sàrl" className="h-14 sm:h-16 lg:h-20 w-auto shrink-0 drop-shadow-[0_10px_25px_rgba(0,0,0,0.45)]" />
              GPN Sàrl
            </span>
          </MaskedLine>
          <MaskedLine delay={0.4} className="text-3xl sm:text-4xl lg:text-6xl text-sky-400">
            {S.hero.line2}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-200"
        >
          {S.hero.paragraph}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#devis"
            onClick={(e) => go(e, "#devis")}
            data-testid="hero-cta-devis"
            className="group inline-flex items-center gap-3 rounded-full bg-sky-600 px-8 py-4 text-sm sm:text-base font-semibold text-white shadow-2xl shadow-sky-950/50 transition-all duration-300 hover:bg-sky-500 hover:-translate-y-0.5"
          >
            {S.hero.cta1}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#realisations"
            onClick={(e) => go(e, "#realisations")}
            data-testid="hero-cta-realisations"
            className="inline-flex items-center rounded-full border border-white/30 px-8 py-4 text-sm sm:text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10 hover:border-white/50"
          >
            {S.hero.cta2}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3"
        >
          <span data-testid="hero-trust-badge" className="inline-flex items-center gap-2 text-sm text-slate-300">
            <BadgeCheck className="w-5 h-5 text-sky-400" />
            {S.hero.trust}
          </span>
          <span className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-400">
            <span className="w-1.5 h-1.5 rotate-45 bg-sky-500" />
            {S.hero.pill2}
          </span>
          <span className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-400">
            <span className="w-1.5 h-1.5 rotate-45 bg-sky-500" />
            {S.hero.pill3}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
          data-testid="hero-stats"
          className="mt-12 flex gap-10 sm:gap-16 border-t border-white/10 pt-7 max-w-xl"
        >
          {S.hero.stats.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{s.value}</p>
              <p className="mt-1 font-tech text-[10px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-sky-400">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 right-6 sm:right-10 z-10 flex gap-2" data-testid="hero-slide-dots">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => setSlide(i)}
            aria-label={`Photo ${i + 1}`}
            className={`h-1 rounded-full transition-all duration-500 ${
              i === slide ? "w-10 bg-sky-400" : "w-4 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      <motion.a
        href="#prestations"
        onClick={(e) => go(e, "#prestations")}
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-slate-400 hover:text-sky-400 transition-colors"
        aria-label={S.hero.scrollAria}
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ChevronDown className="w-6 h-6" />
        </motion.span>
      </motion.a>
    </section>
  );
};
